/* eslint-disable no-unused-vars */
import { requestFail } from '@/store/actions/common.action'
import { getErrorStr } from '@/store/sagas/table.saga'
import { Divider, Drawer, message, Popover, Space, Table, Empty, Steps } from 'antd'
import React, { ReactElement, useRef, useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button } from 'antd'
import {
    StarFilled,
    StarOutlined,
    CheckCircleFilled,
    ClockCircleOutlined,
    LoadingOutlined,
    CloseCircleFilled
} from '@ant-design/icons'
import moment from 'moment'
import { ETechnologyContribution, ETechnologyLeadership } from '@/types'
import { postOneClickUrge } from '@/services/progress.service'
import Title from 'antd/lib/typography/Title'

// 步骤状态枚举
enum EStepStatus {
    WAIT = 'wait',
    PROCESS = 'process',
    FINISH = 'finish',
    ERROR = 'error'
}

// 文本颜色枚举
enum ETextColor {
    PROCESS = '#036fc0',
    WAIT = 'gray',
    SUCCESS = 'green',
    ERROR = 'red'
}

// 步骤索引枚举
enum EStepIndex {
    FEEDBACK = 0, //相关性反馈
    REVIEW_CONFIRM = 1, //评委确认
    PLTMT_REVIEW = 2, //pltmt评审
    TMT_LEADER_REVIEW = 3, //tmt leader评审
    REVIEW_RESULT = 4 //评审结果
}

interface IProps {
    open: boolean;
    setDrawerOpen: (value: boolean) => void;
    data: any;
}

function ReviewProgress(props: IProps): ReactElement {
    const { open, setDrawerOpen, data } = props
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const [urgeLoading, setUrgeLoading] = useState(false)
    const [formObj, setFormObj] = useState(data)
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

    // 计算超时天数
    const getOverdueDays = (endDate: string | Date) => {
        if (!endDate) return 0
        const end = moment(endDate).startOf('day')
        const today = moment().startOf('day')
        return today.isAfter(end) ? today.diff(end, 'days') : 0
    }

    // 判断是否在评审截止日期之前或当天
    const isSameOrBeforeReviewEnd = (time: string | Date) => {
        if (!time) return true
        const reviewEndDate = moment(time).startOf('day')
        const today = moment().startOf('day')
        return today.isSameOrBefore(reviewEndDate)
    }

    // 判断PL TMT用户是否全部评审完成
    const isPLTMTReviewCompleted = useMemo(() => {
        if (!data?.tmtUserList) return false
        const plUsers = data.tmtUserList.filter((user: any) => user.isPL === 1)
        if (plUsers.length === 0) return true // 如果没有PL用户，认为PL阶段已完成
        return plUsers.every((user: any) => user.reviewContent && user.reviewContent.trim() !== '')
    }, [data?.tmtUserList])


    // 判断TMT用户是否全部评审完成
    const isTMTReviewCompleted = useMemo(() => {
        if (!data?.tmtUserList) return false
        const tmtUsers = data.tmtUserList.filter((user: any) => user.isPL !== 1 && user.isPL !== 2)
        if (tmtUsers.length === 0) return true // 如果没有TMT用户，认为TMT阶段已完成
        return tmtUsers.every((user: any) => user.reviewContent && user.reviewContent.trim() !== '')
    }, [data?.tmtUserList])

    // 判断所有评审是否完成
    const isAllReviewCompleted = useMemo(() => {
        return isPLTMTReviewCompleted && isTMTReviewCompleted
    }, [isPLTMTReviewCompleted, isTMTReviewCompleted])

    // 获取当前评审阶段
    const getCurrentReviewPhase = useMemo(() => {
        if (!data?.tmtUserList || (!data.isPlReview && data.reviewType !== 4)) return null

        if (isAllReviewCompleted) {
            return 'completed' // 全部评审完成
        }
        if (!isPLTMTReviewCompleted) {
            // 如果 PL TMT 评审未完成，保持在 PL TMT 评审阶段
            return 'pltmt'
        }

        // PL TMT 评审已完成，
        const dueDate = moment(data.dueDate)
        const fiveDaysAfterDue = dueDate
        const now = moment()
        const isFiveDaysPassed = now.isAfter(fiveDaysAfterDue)
        if (isFiveDaysPassed) {
            // 如果 PL TMT 评审完成且已过 5 天，进入 TMT 评审阶段
            return 'tmt'
        } else {
            // 如果 PL TMT 评审完成但未过 5 天，等待期间保持在 PL TMT 阶段
            return 'pltmt'
        }
    }, [data?.tmtUserList, data?.isPlReview, data?.reviewType, data?.dueDate, isPLTMTReviewCompleted, isTMTReviewCompleted, isAllReviewCompleted])

    // 计算普通提案的当前步骤
    const getNormalProposalStep = useMemo(() => {
        if (!data) return EStepIndex.FEEDBACK

        // 如果评审结果已出
        if (data.reviewResult !== null) return EStepIndex.REVIEW_RESULT

        // 如果评委确认已完成
        if (data.openReview !== null) return EStepIndex.REVIEW_RESULT

        // 如果reviewType为3,直接返回REVIEW_RESULT
        // 技术成果交付注册 有相关的提案，直接跳过相关性反馈，和评委确认，直接显示评审反馈
        // if (data.reviewType === 3) return EStepIndex.REVIEW_RESULT

        // 如果相关性反馈已完成
        if (data.openRelevant === false) return EStepIndex.REVIEW_CONFIRM

        // 默认在第一步
        return EStepIndex.FEEDBACK
    }, [data])

    // 计算PLTMT提案的当前步骤
    const getPlmtProposalStep = useMemo(() => {
        if (!data) return EStepIndex.PLTMT_REVIEW

        // 如果评审结果已出
        if (data.reviewResult !== null) return EStepIndex.REVIEW_RESULT

        // 根据当前评审阶段判断步骤
        const currentPhase = getCurrentReviewPhase
        if (currentPhase === 'completed' || currentPhase === 'tmt') {
            return EStepIndex.REVIEW_RESULT
        } else if (currentPhase === 'pltmt') {
            if (isPLTMTReviewCompleted) {
                return EStepIndex.TMT_LEADER_REVIEW // PL TMT 完成，进入 TMT 等待期
            } else {
                return EStepIndex.PLTMT_REVIEW // PL TMT 评审进行中
            }
        }
        return EStepIndex.PLTMT_REVIEW
    }, [data, getCurrentReviewPhase, isPLTMTReviewCompleted])

    // 计算当前步骤
    const getCurrentStep = useMemo(() => {
        if (!data) return EStepIndex.FEEDBACK

        // 根据提案类型选择对应的步骤计算逻辑
        if (data.isPlReview || data.reviewType === 4) {
            return getPlmtProposalStep
        } else {
            return getNormalProposalStep
        }
    }, [data, getNormalProposalStep, getPlmtProposalStep])

    // 获取相关性反馈的综合信息
    const getFeedbackInfo = useMemo(() => {
        if (!data) {
            return {
                status: EStepStatus.WAIT,
                text: t('waiting'),
                color: ETextColor.WAIT,
                icon: <ClockCircleOutlined />
            }
        }

        // 如果不在评审截止日期之前或当天，或者评审结果已出，则显示为等待状态
        if (data.reviewResult !== null) {
            if (data.openRelevant === false) {
                return {
                    status: EStepStatus.FINISH,
                    text: t('completed'),
                    color: 'inherit',
                    icon: <CheckCircleFilled />
                }
            } else {
                return {
                    status: EStepStatus.WAIT,
                    text: t('waiting'),
                    color: ETextColor.WAIT,
                    icon: <ClockCircleOutlined />
                }
            }
        }

        // 相关性反馈已完成
        if (data.openRelevant === false) {
            return {
                status: EStepStatus.FINISH,
                text: t('completed'),
                color: 'inherit',
                icon: <CheckCircleFilled />
            }
        }

        // 相关性反馈进行中
        return {
            status: EStepStatus.PROCESS,
            text: t('inProgress'),
            color: ETextColor.PROCESS,
            icon: <LoadingOutlined />
        }
    }, [data, data.reviewResult])


    // 获取评委确认的综合信息
    const getReviewConfirmInfo = useMemo(() => {
        if (!data) {
            return {
                status: EStepStatus.WAIT,
                text: t('waiting'),
                color: ETextColor.WAIT,
                icon: <ClockCircleOutlined />
            }
        }

        // 如果相关性反馈未完成，则评委确认处于等待状态
        if (data.openRelevant !== false) {
            return {
                status: EStepStatus.WAIT,
                text: t('waiting'),
                color: ETextColor.WAIT,
                icon: <ClockCircleOutlined />
            }
        }

        // 后端没有判断是否评审确认，导致提案一直在评审中，前端兼容处理
        if (data.reviewResult !== null) {
            return {
                status: EStepStatus.FINISH,
                text: t('completed'),
                color: 'inherit',
                icon: <CheckCircleFilled />
            }
        }

        // 评委确认进行中
        if (data.openReview === null) {
            return {
                status: EStepStatus.PROCESS,
                text: t('inProgress'),
                color: ETextColor.PROCESS,
                icon: <LoadingOutlined />
            }
        }

        // 评委确认已完成
        return {
            status: EStepStatus.FINISH,
            text: t('completed'),
            color: 'inherit',
            icon: <CheckCircleFilled />
        }
    }, [data, isSameOrBeforeReviewEnd, data.reviewResult])

    // 获取PL TMT评审的综合信息
    const getPLTMTReviewInfo = useMemo(() => {
        if (!data || (!data.isPlReview && data.reviewType !== 4)) {
            return {
                status: EStepStatus.WAIT,
                text: t('waiting'),
                color: ETextColor.WAIT,
                icon: <ClockCircleOutlined />
            }
        }

        // 当 isPlReview 为 true 时，不需要等待相关性反馈完成
        if (!data.isPlReview) {
            // 如果相关性反馈未完成，则PL TMT评审处于等待状态
            if (data.openRelevant !== false) {
                return {
                    status: EStepStatus.WAIT,
                    text: t('waiting'),
                    color: ETextColor.WAIT,
                    icon: <ClockCircleOutlined />
                }
            }
        }

        // 如果评审结果已出
        if (data.reviewResult !== null) {
            return {
                status: EStepStatus.FINISH,
                text: t('completed'),
                color: 'inherit',
                icon: <CheckCircleFilled />
            }
        }

        // 根据当前评审阶段判断状态
        const currentPhase = getCurrentReviewPhase

        if (currentPhase === 'completed' || currentPhase === 'tmt') {
            return {
                status: EStepStatus.FINISH,
                text: t('completed'),
                color: 'inherit',
                icon: <CheckCircleFilled />
            }
        } else if (currentPhase === 'pltmt') {
            if (!isPLTMTReviewCompleted) {
                // PL TMT 评审进行中
                const dueDate = moment(data.dueDate)
                const now = moment()
                const remainingHours = dueDate.diff(now, 'hours')
                const remainingDays = Math.floor(remainingHours / 24)
                
                if (remainingHours < 0) {
                    // 超时
                    const overdueHours = Math.abs(remainingHours)
                    const overdueDays = Math.floor(overdueHours / 24)
                    const overdueHoursRemainder = overdueHours % 24
                    
                    if (overdueDays > 0) {
                        return {
                            status: EStepStatus.PROCESS,
                            text: `${t('inProgress')} (${t('LmcRgBy7IatZs7cvtkSYv')} ${overdueDays} ${t('days')})`,
                            color: ETextColor.ERROR,
                            icon: <LoadingOutlined />
                        }
                    } else {
                        return {
                            status: EStepStatus.PROCESS,
                            text: `${t('inProgress')} (${t('LmcRgBy7IatZs7cvtkSYv')} ${overdueHoursRemainder} ${t('Nzj3bCuOuhX8qwr1GKZRh')})`,
                            color: ETextColor.ERROR,
                            icon: <LoadingOutlined />
                        }
                    }
                } else {
                    // 未超时
                    if (remainingDays > 0) {
                        return {
                            status: EStepStatus.PROCESS,
                            text: `${t('inProgress')} ${t('EKTpeQtdzwQBEiYKA2iP2')} (${remainingDays} ${t('days')})`,
                            color: ETextColor.PROCESS,
                            icon: <LoadingOutlined />
                        }
                    } else {
                        return {
                            status: EStepStatus.PROCESS,
                            text: `${t('inProgress')} ${t('EKTpeQtdzwQBEiYKA2iP2')} (${remainingHours} ${t('Nzj3bCuOuhX8qwr1GKZRh')})`,
                            color: ETextColor.PROCESS,
                            icon: <LoadingOutlined />
                        }
                    }
                }
            } else {
                // PL TMT 评审完成
                const dueDate = moment(data.dueDate)
                const now = moment()
                const remainingHours = dueDate.diff(now, 'hours')
                const remainingDays = Math.floor(remainingHours / 24)
                
                if (remainingHours < 0) {
                    // 超时
                    const overdueHours = Math.abs(remainingHours)
                    const overdueDays = Math.floor(overdueHours / 24)
                    const overdueHoursRemainder = overdueHours % 24
                    
                    if (overdueDays > 0) {
                        return {
                            status: EStepStatus.PROCESS,
                            text: `${t('completed')} (${t('LmcRgBy7IatZs7cvtkSYv')} ${overdueDays} ${t('days')})`,
                            color: ETextColor.ERROR,
                            icon: <CheckCircleFilled />
                        }
                    } else {
                        return {
                            status: EStepStatus.PROCESS,
                            text: `${t('completed')} (${t('LmcRgBy7IatZs7cvtkSYv')} ${overdueHoursRemainder} ${t('hours')})`,
                            color: ETextColor.ERROR,
                            icon: <CheckCircleFilled />
                        }
                    }
                } else {
                    // 未超时
                    if (remainingDays > 0) {
                        return {
                            status: EStepStatus.PROCESS,
                            text: `${t('completed')} ${t('EKTpeQtdzwQBEiYKA2iP2')} (${remainingDays} ${t('days')})`,
                            color: ETextColor.PROCESS,
                            icon: <CheckCircleFilled />
                        }
                    } else {
                        return {
                            status: EStepStatus.PROCESS,
                            text: `${t('completed')} ${t('EKTpeQtdzwQBEiYKA2iP2')} (${remainingHours} ${t('hours')})`,
                            color: ETextColor.PROCESS,
                            icon: <CheckCircleFilled />
                        }
                    }
                }
            }
        }

        // 默认等待状态
        return {
            status: EStepStatus.WAIT,
            text: t('waiting'),
            color: ETextColor.WAIT,
            icon: <ClockCircleOutlined />
        }
    }, [data, data?.dueDate, getCurrentReviewPhase, isPLTMTReviewCompleted, t])

    // 获取TMT评审的综合信息
    const getTMTReviewInfo = useMemo(() => {
        if (!data || (!data.isPlReview && data.reviewType !== 4)) {
            return {
                status: EStepStatus.WAIT,
                text: t('waiting'),
                color: ETextColor.WAIT,
                icon: <ClockCircleOutlined />
            }
        }

        // 如果评审结果已出
        if (data.reviewResult !== null) {
            return {
                status: EStepStatus.FINISH,
                text: t('completed'),
                color: 'inherit',
                icon: <CheckCircleFilled />
            }
        }

        // 根据当前评审阶段判断状态
        const currentPhase = getCurrentReviewPhase
        console.log('currentPhase', currentPhase)
        if (currentPhase === 'completed') {
            return {
                status: EStepStatus.FINISH,
                text: t('completed'),
                color: 'inherit',
                icon: <CheckCircleFilled />
            }
        } else if (currentPhase === 'tmt') {
            return {
                status: EStepStatus.PROCESS,
                text: t('inProgress'),
                color: ETextColor.PROCESS,
                icon: <LoadingOutlined />
            }
        } else if (currentPhase === 'pltmt') {
            return {
                status: EStepStatus.WAIT,
                text: t('waiting'),
                color: ETextColor.WAIT,
                icon: <ClockCircleOutlined />
            }
        }

        // 默认等待状态
        return {
            status: EStepStatus.WAIT,
            text: t('waiting'),
            color: ETextColor.WAIT,
            icon: <ClockCircleOutlined />
        }
    }, [data, getCurrentReviewPhase, t])

    // 获取评审结果的综合信息
    const getReviewResultInfo = useMemo(() => {
        if (!data) {
            return {
                status: EStepStatus.WAIT,
                text: t('waiting'),
                color: ETextColor.WAIT,
                icon: <ClockCircleOutlined />
            }
        }

        console.log('Debug getReviewResultInfo:', {
            reviewType: data.reviewType,
            reviewResult: data.reviewResult,
            openRelevant: data.openRelevant,
            openReview: data.openReview,
            isPlReview: data.isPlReview,
            isSameOrBeforeReviewEnd
        })

        // 评审结果已出
        if (data.reviewResult !== null) {
            if (data.reviewResult === true) {
                return {
                    status: EStepStatus.FINISH,
                    text: t('passed'),
                    color: ETextColor.SUCCESS,
                    icon: <CheckCircleFilled />
                }
            } else {
                return {
                    status: EStepStatus.ERROR,
                    text: t('failed'),
                    color: ETextColor.ERROR,
                    icon: <CloseCircleFilled style={{ color: ETextColor.ERROR }} />
                }
            }
        }

        // 当reviewType为3时,直接显示pending状态
        // if (data.reviewType === 3 && !data.isPlReview) {
        //     return {
        //         status: EStepStatus.PROCESS,
        //         text: t('inProgress'),
        //         color: ETextColor.PROCESS,
        //         icon: <LoadingOutlined />
        //     }
        // }

        // 当 isPlReview 为 true 或 reviewType === 4 时的处理逻辑
        if (data.isPlReview || data.reviewType === 4) {
            // 当 isPlReview 为 true 时，不需要等待相关性反馈完成
            if (!data.isPlReview) {
                // 如果相关性反馈未完成，则评审结果处于等待状态
                if (data.openRelevant !== false) {
                    return {
                        status: EStepStatus.WAIT,
                        text: t('waiting'),
                        color: ETextColor.WAIT,
                        icon: <ClockCircleOutlined />
                    }
                }
            }

            // 如果所有评审都未完成，则评审结果处于等待状态
            if (!isAllReviewCompleted) {
                return {
                    status: EStepStatus.WAIT,
                    text: t('waiting'),
                    color: ETextColor.WAIT,
                    icon: <ClockCircleOutlined />
                }
            }

            // 如果超时，显示进行中（超时X天）
            if (!isSameOrBeforeReviewEnd(data.dueDate)) {
                const overdueDays = getOverdueDays(data.dueDate)
                return {
                    status: EStepStatus.PROCESS,
                    text: `${t('inProgress')} (超时 ${overdueDays} 天)`,
                    color: ETextColor.ERROR,
                    icon: <LoadingOutlined />
                }
            }

            // 评审结果进行中
            return {
                status: EStepStatus.PROCESS,
                text: t('inProgress'),
                color: ETextColor.PROCESS,
                icon: <LoadingOutlined />
            }
        }

        // 对于其他 reviewType 的情况（相关性评审流程）
        // 如果相关性反馈未完成或评委确认未完成，则评审结果处于等待状态
        if (data.openRelevant !== false || data.openReview === null) {
            console.log('Debug: Entering wait state because:', {
                openRelevant: data.openRelevant,
                openReview: data.openReview
            })
            return {
                status: EStepStatus.WAIT,
                text: t('waiting'),
                color: ETextColor.WAIT,
                icon: <ClockCircleOutlined />
            }
        }

        // 如果超时，显示进行中（超时X天）
        if (!isSameOrBeforeReviewEnd(data.dueDate)) {
            const overdueDays = getOverdueDays(data.dueDate)
            return {
                status: EStepStatus.PROCESS,
                text: `${t('inProgress')} (超时 ${overdueDays} 天)`,
                color: ETextColor.ERROR,
                icon: <LoadingOutlined />
            }
        }

        // 评审结果进行中
        return {
            status: EStepStatus.PROCESS,
            text: t('inProgress'),
            color: ETextColor.PROCESS,
            icon: <LoadingOutlined />
        }
    }, [data, isSameOrBeforeReviewEnd, isAllReviewCompleted, getOverdueDays, t])

    const technologicalLeadershipArr = useRef([
        {
            label: t('leadership'),
            id: ETechnologyLeadership.LEADING,
        },
        // {
        //     label: t('homeleadership'),
        //     id: ETechnologyLeadership.HOME_LEADING,
        // },
        {
            label: t('equalize'),
            id: ETechnologyLeadership.EQUAL,
        },
        {
            label: t('trail'),
            id: ETechnologyLeadership.TRAIL,
        },
    ])

    const technicalContributionArr = useRef([
        {
            label: t('innovationSellingPoint'),
            id: ETechnologyContribution.INNOVATION,
        },
        {
            label: t('keySellingPoint'),
            id: ETechnologyContribution.KEY,
        },
        {
            label: t('secondarySellingPoints'),
            id: ETechnologyContribution.SECONDARY,
        },
        {
            label: t('infrastructure'),
            id: ETechnologyContribution.BASIC,
        },
    ])

    const onClose = () => {
        setDrawerOpen(false)
    }

    const findLabel = (arr: any[], id: number) =>
        arr.find((item: any) => item.id === id)?.label ?? ''

    // 相关性反馈表格
    const columns = [
        {
            title: () => <span>{t('feedbackName')}</span>,
            dataIndex: 'creatorName',
            key: 'creatorName',
            width: 202,
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('WorkGroup')}</span>,
            dataIndex: 'workGroup',
            key: 'workGroup',
            width: 200,
            ellipsis: { showTitle: true },
            render(value: any) {
                return value?.workGroupName || ''
            }
        },
        {
            title: () => <span>{t('isRelevant')}</span>,
            dataIndex: 'isRelevant',
            key: 'isRelevant',
            width: 120,
            ellipsis: { showTitle: true },
            render(value: boolean, record: any) {
                if (record.auditOrNot) {
                    return <span>{t('sitInOn')}</span>
                } else {
                    if (value !== null) {
                        if (value) {
                            return <span>{t('Yes')}</span>
                        }
                        return <span>{t('No')}</span>
                    } else {
                        return <></>
                    }
                }
            }
        },
        {
            title: () => <span>{t('tmtUser')}</span>,
            dataIndex: 'tmtInfos',
            key: 'tmtInfos',
            width: 250,
            ellipsis: { showTitle: true },
            render(value: any[]) {
                if (value && value.length > 0) {
                    return value.map(item => item?.tmtFullUser).join(',')
                } else {
                    return ''
                }
            }
        },
        {
            title: () => <span>{t('description')}</span>,
            dataIndex: 'description',
            key: 'description',
            // width: 150,
            ellipsis: { showTitle: true },
            render(value: string) {
                return <Popover
                    placement="topLeft"
                    title=""
                    content={
                        <div style={{ maxWidth: 240, maxHeight: 300, overflowY: 'auto', wordBreak: 'break-all' }}
                            dangerouslySetInnerHTML={{
                                __html: value ? ((value.includes('"str":') || value.includes('"obj":')) ? JSON.parse(value)?.str : value) : ''
                            }}
                        ></div>
                    }
                >
                    <div style={{ maxWidth: 240 }}
                        className='table_ellipsis'
                        dangerouslySetInnerHTML={{
                            __html: value ? ((value.includes('"str":') || value.includes('"obj":')) ? JSON.parse(value)?.str : value) : ''
                        }}
                    ></div>
                </Popover>
            }
        },
    ]

    // 评审反馈
    const tmtColumns = [
        {
            title: () => <span>{t('feedbackName')}</span>,
            dataIndex: 'tmtFullUser',
            key: 'tmtFullUser',
            width: 100,
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('WorkGroup')}</span>,
            dataIndex: 'workGroup',
            key: 'workGroup',
            width: 150,
            ellipsis: { showTitle: true },
            render(value: any) {
                return value?.workGroupName || ''
            }
        },
        {
            title: () => <span>{t('feedbackName') + (i18n.language !== 'zh' ? ' ' : '') + t('type')}</span>,
            dataIndex: 'isPL',
            key: 'isPL',
            width: 100,
            ellipsis: { showTitle: true },
            render(value: number) {
                // 增加除了PL TMT之外的都是TMT
                return ((value === 1 || value === 2) ? <span>PL TMT</span> : <span>TMT</span>)
            }
        },
        {
            title: () => <span>{t('technologicalLeadership')}</span>,
            dataIndex: 'technologyLeadership',
            key: 'technologyLeadership',
            width: 150,
            ellipsis: { showTitle: true },
            render(value: number, record: any) {
                if (!value && formObj) {
                    if (record.tmtFullUser === formObj.creator) {
                        return findLabel(technologicalLeadershipArr.current, formObj.technologyLeadership)
                    }
                }
                return findLabel(technologicalLeadershipArr.current, value)
            }
        },
        {
            title: () => <span>{t('technicalContribution')}</span>,
            dataIndex: 'technologyContribution',
            key: 'technologyContribution',
            width: 120,
            ellipsis: { showTitle: true },
            render(value: number, record: any) {
                if (!value && formObj) {
                    if (record.tmtFullUser === formObj.creator) {
                        return findLabel(technicalContributionArr.current, formObj.technologyContribution)
                    }
                }
                return findLabel(technicalContributionArr.current, value)
            }
        },
        {
            title: () => <span>{t('techType')}</span>,
            dataIndex: 'coreTechnology',
            key: 'coreTechnology',
            width: 120,
            ellipsis: { showTitle: true },
            render(value: number, record: any) {
                const labelArr = [t('core') + (i18n.language !== 'zh' ? ' ' : '') + t('technology'),
                    t('key') + (i18n.language !== 'zh' ? ' ' : '') + t('technology'),
                    t('basicTechnology'),
                    t('reserveTechnology'),
                ]
                if (!value && formObj) {
                    if (record.tmtFullUser === formObj.creator) {
                        return labelArr[formObj.coreTechnology]
                    }
                }
                return labelArr[value]
            }
        },
        {
            title: () => <span>{t('reviewResultTrue')}</span>,
            dataIndex: 'isPassed',
            key: 'isPassed',
            width: 100,
            ellipsis: { showTitle: true },
            render(value: boolean | null,record:any) {
                if (value !== null) {
                    if (value) {
                        return <span>{t('Yes')}</span>
                    } else  {
                        return <span>{t('No')}</span>
                    }
                } else if(record.reviewContent?.length > 0 && record.judgesType !== 2) {
                    return <span>{t('waiver')}</span>
                } else {
                    return <></>
                }
            }
        },
        {
            title: () => <span>{t('wcYOCuH76At1ckcMdzKKf')}</span>,
            dataIndex: 'judgesType',
            key: 'judgesType',
            width: 100,
            ellipsis: { showTitle: true },
            render(value: number | null) {
                if(value === 1 ){
                    return <span>{t('T33AqK8KbZMESLZl4j41q')}</span>
                }else if(value === 2 ){
                    return <span>{t('XyU0VPlblKWVpW6SokzAQ')}</span>
                }else {
                    return <></>
                }
            }
        },
        {
            title: () => <span>{t('tmtMail')}</span>,
            dataIndex: 'tmtMail',
            key: 'tmtMail',
            width: 150,
            ellipsis: { showTitle: true }
        },
        {
            title: () => <span>{t('reviewMeeting') + (i18n.language !== 'zh' ? ' ' : '') + t('date')}</span>,
            dataIndex: 'modifyTime',
            key: 'modifyTime',
            width: 100,
            ellipsis: { showTitle: true },
            render(value: number | null) {
                return value ? moment(value).format('YYYY-MM-DD') : ''
            }
        },
        {
            title: () => <span>{t('reviewContent')}</span>,
            dataIndex: 'reviewContent',
            key: 'reviewContent',
            // width: 150,
            ellipsis: { showTitle: true },
            render(value: string) {
                return <Popover
                    placement="topLeft"
                    title=""
                    content={
                        <div style={{ maxWidth: 400, maxHeight: 300, overflowY: 'auto', wordBreak: 'break-all' }}
                            dangerouslySetInnerHTML={{
                                __html: value ? ((value.includes('"str":') || value.includes('"obj":')) ? JSON.parse(value)?.str : value) : ''
                            }}
                        ></div>
                    }
                >
                    <div style={{ maxWidth: 400 }}
                        className='table_ellipsis'
                        dangerouslySetInnerHTML={{
                            __html: value ? ((value.includes('"str":') || value.includes('"obj":')) ? JSON.parse(value)?.str : value) : ''
                        }}
                    ></div>
                </Popover>
            },
        },
        {
            title: () => <span>{t('isCrossResult')}</span>,
            dataIndex: 'isCross',
            key: 'isCross',
            width: 100,
            ellipsis: { showTitle: true },
            render(value: boolean | null) {
                if (value !== null) {
                    if (value) {
                        return <span>{t('Yes')}</span>
                    } else {
                        return <span>{t('No')}</span>
                    }
                } else {
                    return <></>
                }
            }
        },
        {
            title: () => <span>{t('scopeDetail')}</span>,
            dataIndex: 'need',
            key: 'need',
            ellipsis: { showTitle: true },
            render(_: string, record: any) {
                if (record.judgesType === 1) {
                    return <>
                        <span className='star_label'>{t('need')}: </span>
                        {Array(10).fill(1).map((_, index) => {
                            if (index < record.need) {
                                return (<StarFilled style={{ fontSize: 18, color: '#efdd09', marginLeft: 5 }}
                                    className='star'
                                    key={index}
                                />)
                            } else {
                                return (<StarOutlined style={{ fontSize: 18, marginLeft: 5 }}
                                    className='star'
                                    key={index}
                                />)
                            }
                        })}<br />
                        <span className='star_label'>{t('maturityStar')}: </span>
                        {Array(10).fill(1).map((_, index) => {
                            if (index < record.maturity) {
                                return (<StarFilled style={{ fontSize: 18, color: '#efdd09', marginLeft: 5 }}
                                    className='star'
                                    key={index}
                                />)
                            } else {
                                return (<StarOutlined style={{ fontSize: 18, marginLeft: 5 }}
                                    className='star'
                                    key={index}
                                />)
                            }
                        })}<br />
                        <span className='star_label'>{t('diffrentiation')}: </span>
                        {Array(10).fill(1).map((_, index) => {
                            if (index < record.diffrentiation) {
                                return (<StarFilled style={{ fontSize: 18, color: '#efdd09', marginLeft: 5 }}
                                    className='star'
                                    key={index}
                                />)
                            } else {
                                return (<StarOutlined style={{ fontSize: 18, marginLeft: 5 }}
                                    className='star'
                                    key={index}
                                />)
                            }
                        })}<br />
                        <span className='star_label'>{t('costSaving')}: </span>
                        {Array(10).fill(1).map((_, index) => {
                            if (index < record.costSaving) {
                                return (<StarFilled style={{ fontSize: 18, color: '#efdd09', marginLeft: 5 }}
                                    className='star'
                                    key={index}
                                />)
                            } else {
                                return (<StarOutlined style={{ fontSize: 18, marginLeft: 5 }}
                                    className='star'
                                    key={index}
                                />)
                            }
                        })}
                    </>
                } else {
                    return <></>
                }
            }
        }
    ]

    // 使用 useMemo 优化列过滤
    const filteredColumns = useMemo(() => {
        return tmtColumns.filter(v => {
            if (v.key === 'need') {
                return (formObj?.reviewType === 1 && formObj?.isDisruptive)
            } else if (v.key === 'isCross') {
                return (formObj?.reviewType === 1 && formObj?.isCross)
            } else if (v.key === 'workGroup' || v.key === 'isParticipate') {
                return formObj?.reviewType !== 4
            } else if (v.key === 'technologyLeadership' || v.key === 'technologyContribution' || v.key === 'coreTechnology') {
                return formObj?.reviewType !== 1
            } else if (v.key === 'isPassed') {
                return getCurrentStep === EStepIndex.REVIEW_RESULT
            }
            else {
                return true
            }
        })
    }, [formObj, tmtColumns, t, getCurrentStep])

    // 定义行选择配置
    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[], selectedRows: any[]) => {
            // 将选中行的 tmtUser 值作为 selectedRowKeys
            const tmtUserKeys = selectedRows.map(row => row.tmtUser)
            setSelectedRowKeys(tmtUserKeys)
        },
        getCheckboxProps: (record: any) => ({
            disabled: record.isPassed !== null, // 当 isPassed 为 true 时禁用复选框
            name: record.tmtFullUser,
        }),
    }

    const handleUrge = async () => {
        if (selectedRowKeys.length === 0) {
            return
        }
        try {
            if (urgeLoading || !data?.id) return  // 检查 data.id 是否存在
            setUrgeLoading(true)

            const params = {
                reviewId: data.id,
                userList: selectedRowKeys as string[],
            }
            const res = await postOneClickUrge(params)
            if (res === null) {
                message.success(t('urgeSuccess'))
            }
        } catch (e: any) {
            const errorStr = getErrorStr(e)
            dispatch(requestFail(errorStr))
        } finally {
            setUrgeLoading(false)
        }
    }
    // 能看到评审进度的提案都为 评审中，所以不需要判断评审阶段
    // 是否是pltmt提案
    const isPlmtProposal = useMemo(() => data?.reviewType === 4 || data?.isPlReview, [data])

    const renderCurrentTitle = useMemo(() => {
        switch (getCurrentStep) {
            case EStepIndex.FEEDBACK:
                return <Divider plain>{t('feedback')}</Divider>
            case EStepIndex.REVIEW_CONFIRM:
                return <Divider plain>{t('reviewConfirm')}</Divider>
            case EStepIndex.PLTMT_REVIEW:
                return <Divider plain>{t('pltmtReview')}</Divider>
            case EStepIndex.TMT_LEADER_REVIEW:
                return <Divider plain>{t('tmtLeaderReview')}</Divider>
            case EStepIndex.REVIEW_RESULT:
                return <Divider plain>{t('joinPerson')}</Divider>
            default:
                return <></>
        }
    }, [isPlmtProposal, data?.reviewType])

    const renderCurrentContent = useMemo(() => {
        switch (getCurrentStep) {
            case EStepIndex.FEEDBACK:
                return <Table
                    bordered
                    scroll={{ x: 'max-content' }}
                    rowKey="tmtUser"
                    dataSource={data?.itmtUserList.filter((item: any) => item.isRelevant !== null || item.auditOrNot !== null)}
                    columns={columns}
                    pagination={false}
                />
            case EStepIndex.REVIEW_CONFIRM:
                return <Table
                    bordered
                    scroll={{ x: 'max-content' }}
                    rowKey="tmtUser"
                    dataSource={[{ tmtFullUser: data?.creator }]}
                    columns={filteredColumns}
                    pagination={false}
                />
            default:
                return <Table
                    bordered
                    scroll={{ x: 'max-content' }}
                    rowKey="tmtUser"
                    dataSource={data?.tmtUserList}
                    rowSelection={rowSelection}
                    columns={filteredColumns}
                    pagination={false}
                />
        }
    }, [getCurrentStep, data?.tmtUserList, data?.itmtUserList, filteredColumns, columns, rowSelection, t, data?.reviewType])

    useEffect(() => {
        setFormObj(data)
    }, [data])

    if (!data) {
        return <Empty description={t('noData')} />
    }


    return (
        <Drawer
            width={640}
            title={t('review progress')}
            placement="right"
            onClose={onClose}
            visible={open}
        >
            <div>
                <Title level={5}>{data?.proposalName}</Title>
                <Steps
                    direction="vertical"
                    style={{ marginTop: 32 }}
                    current={getCurrentStep}
                >
                    <>
                        {!isPlmtProposal && (
                            <>
                                {/* 相关性反馈 */}
                                <Steps.Step
                                    title={t('feedback')}
                                    status={getFeedbackInfo.status}
                                    description={
                                        <div>
                                            <span style={{ color: getFeedbackInfo.color }}>
                                                {getFeedbackInfo.text}
                                            </span>
                                        </div>
                                    }
                                    icon={getFeedbackInfo.icon}
                                />
                                {/* 评委确认 */}
                                <Steps.Step
                                    title={t('reviewConfirm')}
                                    status={getReviewConfirmInfo.status}
                                    description={
                                        <div>
                                            <span style={{ color: getReviewConfirmInfo.color }}>
                                                {getReviewConfirmInfo.text}
                                            </span>
                                        </div>
                                    }
                                    icon={getReviewConfirmInfo.icon}
                                />
                                <Steps.Step
                                    title={t('startReview')}
                                    status={getReviewResultInfo.status}
                                    description={
                                        <div>
                                            <span style={{ color: getReviewResultInfo.color }}>
                                                {getReviewResultInfo.text}
                                            </span>
                                        </div>
                                    }
                                    icon={getReviewResultInfo.icon}
                                />
                            </>
                        )}

                        {isPlmtProposal && (
                            <>
                                {/* pltmt评审 */}
                                <Steps.Step
                                    title={t('pltmtReview')}
                                    status={getPLTMTReviewInfo.status}
                                    description={
                                        <div>
                                            <span style={{ color: getPLTMTReviewInfo.color }}>
                                                {getPLTMTReviewInfo.text}
                                            </span>
                                        </div>
                                    }
                                    icon={getPLTMTReviewInfo.icon}
                                />
                                {/* tmt leader评审 */}
                                <Steps.Step
                                    title={t('tmtLeaderReview')}
                                    status={getTMTReviewInfo.status}
                                    description={
                                        <div>
                                            <span style={{ color: getTMTReviewInfo.color }}>
                                                {getTMTReviewInfo.text}
                                            </span>
                                        </div>
                                    }
                                    icon={getTMTReviewInfo.icon}
                                />
                            </>
                        )}

                        {/* 评审结果 */}

                    </>
                </Steps>
            </div>
            {renderCurrentTitle}
            {renderCurrentContent}
            {
                isPlmtProposal || getCurrentStep === EStepIndex.REVIEW_RESULT && (
                    <Space style={{ width: '100%', marginTop: 16 }} direction='vertical' align='end'>
                        <Button onClick={handleUrge} disabled={selectedRowKeys.length === 0} loading={urgeLoading} type='primary'>{t('oneClickUrge')}</Button>
                    </Space>
                )
            }
        </Drawer>
    )
}

export default ReviewProgress
